<template>
	<div class="playground">
		<div class="map-container">
			<mgl-map
				:map-style="mapStyleURL(ALIDADE_SMOOTH)"
				:center="CENTER"
				:zoom="10"
				@map:load="onMapLoad"
			>
				<mgl-geo-json-source source-id="grid-source" :data="gridFC">
					<mgl-circle-layer
						layer-id="grid-points"
						:paint="{
							'circle-radius': 5,
							'circle-color': '#3b82f6',
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1,
						}"
					/>
					<mgl-circle-layer
						v-if="selectedIds.size > 0"
						layer-id="grid-points-selected"
						:filter="selectedFilter"
						:paint="{
							'circle-radius': 7,
							'circle-color': '#f59e0b',
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 2,
						}"
					/>
				</mgl-geo-json-source>

				<LassoSelect
					v-model:active="lassoActive"
					layers="grid-points"
					@select="onLassoSelect"
					@cancel="lassoActive = false"
				/>
			</mgl-map>
		</div>

		<div class="panel">
			<div class="panel-header">
				<div class="d-flex align-items-center gap-2">
					<button
						class="btn btn-sm"
						:class="lassoActive ? 'btn-primary' : 'btn-outline-primary'"
						@click="lassoActive = !lassoActive"
					>
						<i class="bi bi-bounding-box me-1"></i>Lasso
					</button>
					<button
						class="btn btn-sm btn-outline-secondary"
						:disabled="selectedIds.size === 0"
						@click="selectedIds = new Set()"
					>
						<i class="bi bi-x-circle me-1"></i>Clear
					</button>
					<span class="badge text-bg-info"
						>{{ selectedIds.size }} /
						{{ gridFC.features.length }} selected</span
					>
					<span class="small text-muted ms-auto">
						Drag = replace &middot; Shift+drag = add &middot; Alt+drag = remove
						&middot; Esc = cancel
					</span>
				</div>
			</div>

			<div class="panel-body">
				<table class="table table-sm table-striped align-middle mb-0">
					<thead>
						<tr>
							<th>#</th>
							<th>Name</th>
							<th class="text-end">Longitude</th>
							<th class="text-end">Latitude</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in selectedRows" :key="row.id">
							<td>{{ row.id }}</td>
							<td>{{ row.name }}</td>
							<td class="text-end font-monospace">{{ row.lng.toFixed(4) }}</td>
							<td class="text-end font-monospace">{{ row.lat.toFixed(4) }}</td>
						</tr>
						<tr v-if="selectedRows.length === 0">
							<td colspan="4" class="text-center text-muted py-3">
								No points selected. Arm the lasso tool and drag a shape on the
								map.
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { MglMap, MglGeoJsonSource, MglCircleLayer } from "vue-maplibre-gl";
import { mapStyleURL } from "@/map/util";
import { ALIDADE_SMOOTH } from "@/map/constants";
import LassoSelect from "@/map/LassoSelect.vue";
import type { LassoSelectResult } from "@/map/lasso";
import type { GeoJSON } from "geojson";
import type { Map as MaplibreMap } from "maplibre-gl";

// Debug hook for console/browser-automation exploration in this sandbox.
function onMapLoad(event: { map: MaplibreMap }) {
	(window as any).__uiMap = event.map;
}

const CENTER: [number, number] = [-119.7, 36.8];
const GRID_ROWS = 15;
const GRID_COLS = 15;
const GRID_SPACING = 0.025; // degrees between points

interface GridProps {
	id: number;
	name: string;
}

function buildGrid(): GeoJSON.FeatureCollection<GeoJSON.Point, GridProps> {
	const features: GeoJSON.Feature<GeoJSON.Point, GridProps>[] = [];
	for (let r = 0; r < GRID_ROWS; r++) {
		for (let c = 0; c < GRID_COLS; c++) {
			const id = r * GRID_COLS + c;
			features.push({
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [
						CENTER[0] + (c - (GRID_COLS - 1) / 2) * GRID_SPACING,
						CENTER[1] + (r - (GRID_ROWS - 1) / 2) * GRID_SPACING,
					],
				},
				properties: { id, name: `point-${id}` },
			});
		}
	}
	return { type: "FeatureCollection", features };
}

const gridFC = buildGrid();
const gridById = new Map(gridFC.features.map((f) => [f.properties.id, f]));

const lassoActive = ref(false);
const selectedIds = ref<Set<number>>(new Set());

const selectedFilter = computed(() =>
	selectedIds.value.size > 0
		? ([
				"in",
				["get", "id"],
				["literal", [...selectedIds.value].sort((a, b) => a - b)],
			] as any)
		: (["in", ["get", "id"], ["literal", []]] as any),
);

const selectedRows = computed(() => {
	const rows: { id: number; name: string; lng: number; lat: number }[] = [];
	for (const id of [...selectedIds.value].sort((a, b) => a - b)) {
		const f = gridById.get(id);
		if (!f) continue;
		const [lng, lat] = f.geometry.coordinates;
		rows.push({ id, name: f.properties.name, lng, lat });
	}
	return rows;
});

function onLassoSelect({ features, action }: LassoSelectResult) {
	const ids = new Set<number>();
	for (const f of features) {
		const id = f.properties?.id;
		if (typeof id === "number") ids.add(id);
	}
	if (action === "reset") {
		selectedIds.value = ids;
	} else if (action === "append") {
		selectedIds.value = new Set([...selectedIds.value, ...ids]);
	} else {
		const next = new Set(selectedIds.value);
		for (const id of ids) next.delete(id);
		selectedIds.value = next;
	}
}
</script>

<style scoped>
/* Flow layout inside the app's #content column (the 250px sidebar offset is
 * handled globally) — absolute positioning would anchor to the viewport and
 * slide under the fixed sidebar. */
.playground {
	display: flex;
	flex-direction: column;
	height: calc(100vh - 20px); /* #content has 10px padding top/bottom */
	font-family:
		-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.map-container {
	flex: 1;
	position: relative;
	min-height: 0;
}

.panel {
	height: 240px;
	background: #fff;
	border-top: 1px solid #dee2e6;
	display: flex;
	flex-direction: column;
	z-index: 1;
}

.panel-header {
	padding: 10px 16px;
	border-bottom: 1px solid #dee2e6;
	background: #f8f9fa;
}

.panel-body {
	flex: 1;
	overflow-y: auto;
	padding: 0;
}
</style>
